import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Neurological subsection adapter
 * Handles neurological assessment including PHQ2 scoring and seizure management (15 fields)
 */
export class NeurologicalAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Headache assessment
    const headaches = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES
    )?.at(0)

    const headacheType = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE
    )

    // Seizure assessment
    const seizures = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES
    )?.at(0)

    const seizureDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE
    )?.at(0)

    const seizureType = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE
    )

    const seizurePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS
    )

    // General neurological status
    const neuroStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS
    )

    const alteredAffect = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT
    )?.at(0)

    const alteredAffectFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS
    )

    // PHQ2 Depression Screening (Patient Health Questionnaire-2)
    const phq2Screening = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING
    )?.at(0)

    const phq2Notes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES
    )?.at(0)

    // PHQ2 individual questions (scored 0-3 each)
    const interest = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST
    )?.at(0)

    const depressed = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED
    )?.at(0)

    // Caregiver assessment
    const caregiverDifficulty = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.NEURO_HEADACHES]: headaches,
      [RocFormField.NEURO_HEADACHE_TYPE]: headacheType,
      [RocFormField.NEURO_SEIZURES]: seizures,
      [RocFormField.NEURO_SEIZURE_DATE]: seizureDate,
      [RocFormField.NEURO_SEIZURE_TYPE]: seizureType,
      [RocFormField.NEURO_SEIZURE_PRECAUTIONS]: seizurePrecautions,
      [RocFormField.NEURO_STATUS]: neuroStatus,
      [RocFormField.NEURO_ALTERED_AFFECT]: alteredAffect,
      [RocFormField.NEURO_ALTERED_AFFECT_FACTORS]: alteredAffectFactors,
      [RocFormField.NEURO_PHQ2_SCREENING]: phq2Screening,
      [RocFormField.NEURO_PHQ2_NOTES]: phq2Notes,
      [RocFormField.NEURO_INTEREST]: interest,
      [RocFormField.NEURO_DEPRESSED]: depressed,
      [RocFormField.NEURO_CAREGIVER_DIFFICULTY]: caregiverDifficulty,
      [RocFormField.NEURO_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.NEURO_HEADACHES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES,
        value: this.getFormDataField(RocFormField.NEURO_HEADACHES)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_HEADACHE_TYPE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE,
        value: this.getFormDataField(RocFormField.NEURO_HEADACHE_TYPE)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_SEIZURES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES,
        value: this.getFormDataField(RocFormField.NEURO_SEIZURES)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_SEIZURE_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE,
        value: this.getFormDataField(RocFormField.NEURO_SEIZURE_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_SEIZURE_TYPE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE,
        value: this.getFormDataField(RocFormField.NEURO_SEIZURE_TYPE)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_SEIZURE_PRECAUTIONS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS,
        value: this.getFormDataField(RocFormField.NEURO_SEIZURE_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_STATUS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS,
        value: this.getFormDataField(RocFormField.NEURO_STATUS)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_ALTERED_AFFECT)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT,
        value: this.getFormDataField(RocFormField.NEURO_ALTERED_AFFECT)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_ALTERED_AFFECT_FACTORS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS,
        value: this.getFormDataField(RocFormField.NEURO_ALTERED_AFFECT_FACTORS)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_PHQ2_SCREENING)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING,
        value: this.getFormDataField(RocFormField.NEURO_PHQ2_SCREENING)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_PHQ2_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES,
        value: this.getFormDataField(RocFormField.NEURO_PHQ2_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_INTEREST)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST,
        value: this.getFormDataField(RocFormField.NEURO_INTEREST)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_DEPRESSED)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED,
        value: this.getFormDataField(RocFormField.NEURO_DEPRESSED)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_CAREGIVER_DIFFICULTY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY,
        value: this.getFormDataField(RocFormField.NEURO_CAREGIVER_DIFFICULTY)
      })
    }

    if (this.formDataFieldExists(RocFormField.NEURO_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.NEURO_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
